import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Progress, Row, Segmented, Skeleton, Space, Statistic, Table, Tag, Typography } from 'antd';
import {
  AlertOutlined, BankOutlined, CheckCircleOutlined, ClockCircleOutlined, DesktopOutlined,
  ReloadOutlined, RiseOutlined, TeamOutlined, UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Title, Text } = Typography;
const metricLabels = { schools: '新增学校', classes: '新增班级', teachers: '新增教师', students: '新增学生', messages: '消息发送' };
const severityColor = { high: 'red', medium: 'orange', low: 'blue' };

export default function PlatformOperationsDashboard({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState(7);

  const load = async () => {
    setLoading(true); setError('');
    try { setDashboard((await api.get('/platform-dashboard')).data?.data || null); }
    catch (requestError) { setError(requestError.response?.data?.message || '平台运营数据加载失败'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = dashboard?.totals || {};
  const selectedPeriod = useMemo(() => (dashboard?.periods || []).find((item) => item.days === period) || {}, [dashboard, period]);
  const segments = Object.fromEntries((dashboard?.schoolSegments || []).map((item) => [item.segment, item]));
  const standard = segments.standard || {};
  const legacy = segments.legacy || {};

  if (loading) return <Skeleton active paragraph={{ rows: 14 }} />;
  if (error) return <Alert type="error" showIcon message={error} action={<Button onClick={load}>重新加载</Button>} />;

  const segmentColumns = [
    { title: '学校类型', dataIndex: 'segment', render: (value) => value === 'standard' ? <Tag color="green">标准学校</Tag> : <Tag color="gold">历史传统学校</Tag> },
    { title: '学校', dataIndex: 'schools' }, { title: '班级', dataIndex: 'classes' }, { title: '教师', dataIndex: 'teachers' },
    { title: '学生', dataIndex: 'students' }, { title: '设备', dataIndex: 'devices' }, { title: '在线班级', dataIndex: 'onlineClasses' }
  ];

  return <div style={embedded
    ? { background: '#f5f7fa', margin: -16, padding: 20 }
    : { background: '#f5f7fa', margin: -24, padding: 24, minHeight: '100vh' }}>
    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col><Title level={2} style={{ margin: 0 }}>平台运营驾驶舱</Title><Text type="secondary">关注学校标准化、业务增长、数据质量和需要平台介入的事项</Text></Col>
      <Col><Space><Text type="secondary">数据生成于 {new Date(dashboard.generatedAt).toLocaleString()}</Text><Button icon={<ReloadOutlined />} onClick={load}>刷新</Button></Space></Col>
    </Row>

    <section style={{ background: '#fff', padding: '22px 24px', borderRadius: 12, marginBottom: 16 }}>
      <Row gutter={[24, 24]}>
        <Col xs={12} md={8} xl={4}><Statistic title="学校总数" value={totals.schools || 0} prefix={<BankOutlined />} /></Col>
        <Col xs={12} md={8} xl={4}><Statistic title="标准学校" value={totals.standardSchools || 0} valueStyle={{ color: '#237804' }} /></Col>
        <Col xs={12} md={8} xl={4}><Statistic title="历史传统学校" value={totals.legacySchools || 0} valueStyle={{ color: '#ad6800' }} /></Col>
        <Col xs={12} md={8} xl={4}><Statistic title="教师" value={totals.teachers || 0} prefix={<TeamOutlined />} /></Col>
        <Col xs={12} md={8} xl={4}><Statistic title="学生" value={totals.students || 0} prefix={<UserOutlined />} /></Col>
        <Col xs={12} md={8} xl={4}><Statistic title="在线班级" value={totals.onlineClasses || 0} suffix={`/ ${totals.classes || 0}`} prefix={<DesktopOutlined />} /></Col>
      </Row>
    </section>

    <Row gutter={[16, 16]}>
      <Col xs={24} xl={15}>
        <section style={{ background: '#fff', padding: 24, borderRadius: 12, height: '100%' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 18 }}><Col><Title level={4} style={{ margin: 0 }}>业务变化</Title><Text type="secondary">与上一相同周期比较</Text></Col><Col><Segmented value={period} onChange={setPeriod} options={[{ label: '今日', value: 1 }, { label: '7日', value: 7 }, { label: '30日', value: 30 }]} /></Col></Row>
          <Row gutter={[16, 24]}>{Object.entries(metricLabels).map(([key, label]) => {
            const item = selectedPeriod[key] || {};
            return <Col xs={12} lg={8} key={key}><div style={{ borderLeft: `3px solid ${Number(item.change) >= 0 ? '#1677ff' : '#ff4d4f'}`, paddingLeft: 14 }}><Text type="secondary">{label}</Text><div style={{ fontSize: 26, fontWeight: 650 }}>{item.current || 0}</div><Text type={Number(item.change) < 0 ? 'danger' : 'success'}><RiseOutlined /> {Number(item.change) >= 0 ? '+' : ''}{item.change || 0}（{item.rate || 0}%）</Text></div></Col>;
          })}</Row>
        </section>
      </Col>
      <Col xs={24} xl={9}>
        <section style={{ background: '#fff', padding: 24, borderRadius: 12, height: '100%' }}>
          <Title level={4} style={{ marginTop: 0 }}>标准化进度</Title>
          <Progress type="circle" percent={totals.schools ? Math.round((totals.standardSchools || 0) / totals.schools * 100) : 0} />
          <div style={{ marginTop: 18 }}><Text>标准学校 {standard.schools || 0} 所，覆盖教师 {standard.teachers || 0} 人</Text></div>
          <div style={{ marginTop: 8 }}><Text>历史传统学校 {legacy.schools || 0} 所，待完成官方映射</Text></div>
          <Button type="link" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/schools')}>进入标准学校管理</Button>
        </section>
      </Col>
    </Row>

    <section style={{ background: '#fff', padding: 24, borderRadius: 12, marginTop: 16 }}>
      <Row justify="space-between"><Col><Title level={4} style={{ marginTop: 0 }}>平台待处理</Title><Text type="secondary">共 {dashboard.actionTotal || 0} 项需要关注</Text></Col><Col><AlertOutlined style={{ fontSize: 24, color: dashboard.actionTotal ? '#ff4d4f' : '#52c41a' }} /></Col></Row>
      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>{(dashboard.actionItems || []).map((item) => <Col xs={24} md={12} xl={8} key={item.key}><Button block style={{ height: 'auto', padding: '14px 16px', textAlign: 'left' }} onClick={() => navigate(item.route)}><Row justify="space-between"><Col><Tag color={severityColor[item.severity]}>{item.severity === 'high' ? '优先处理' : '需要关注'}</Tag><Text strong>{item.title}</Text></Col><Col><Text strong style={{ fontSize: 20 }}>{item.value}</Text></Col></Row></Button></Col>)}</Row>
    </section>

    <section style={{ background: '#fff', padding: 24, borderRadius: 12, marginTop: 16 }}>
      <Title level={4} style={{ marginTop: 0 }}>学校类型对比</Title><Table rowKey="segment" pagination={false} columns={segmentColumns} dataSource={dashboard.schoolSegments || []} />
    </section>

    <section style={{ background: '#fff', padding: 24, borderRadius: 12, marginTop: 16 }}>
      <Title level={4} style={{ marginTop: 0 }}>平台健康</Title><Space wrap size={[16, 12]}>{Object.entries(dashboard.health || {}).map(([key, value]) => <Tag key={key} icon={value === 'healthy' ? <CheckCircleOutlined /> : <ClockCircleOutlined />} color={value === 'healthy' ? 'success' : 'warning'}>{({ database: '数据库', realtime: '实时推送', devices: '桌面设备', storage: '文件存储' }[key] || key)}：{value === 'healthy' ? '正常' : '需关注'}</Tag>)}</Space>
    </section>
  </div>;
}
